<template>
  <ul
    class="link-list"
    :class="{ 'desktop-mode': apiConfig.viewerMode === 'desktop' }"
  >
    <li
      v-for="url in urls"
      :key="url.href"
      class="link"
    >
      <a
        :href="url.href"
        target="_blank"
      >{{ url.hostname.split("0").reverse()[0] }}</a>
      <!-- eslint-disable-next-line prettier/prettier -->
      <a
        class="qr-link"
        :href="buildUrlWithParams('https://ddg.gg/', {q: `qr ${url.href}`}).href"
        target="_blank"
      >
        <SvgSymbol
          symbol="qr"
        />
      </a>
    </li>
    <li>
      <a
        :href="source.src"
        target="_blank"
      >Raw Image</a>
    </li>
    <li>
      <a
        @click="notification(source.id)"
        target="_blank"
      >Open in Hydrus</a>
    </li>
  </ul>
</template>
<script setup lang="ts">
import { computed } from "vue";
import SvgSymbol from "@/components/Svg/SvgSymbol.vue";
import { buildUrlWithParams } from "@/requests/url";
import { type Image, useApiStore } from "@/stores/api";
import { useFetch } from "@/libraries/vue-fetch/composables/useFetch";

const apiConfig = useApiStore();

const props = defineProps<{
  urls: string[];
  source: Image;
}>();

const urls = computed<URL[]>(() =>
  !props.urls
    ? []
    : props.urls.map((url: string) => new URL(url)),
);

/**
 * Open a popup which will lead you to this file.
 * Manage Popups permission needed.
 * @param file_id
 * @see https://hydrusnetwork.github.io/hydrus/developer_api.html#manage_popups_add_popuip
 * @see https://hydrusnetwork.github.io/hydrus/developer_api.html#job_status_objects
 */
async function notification(file_id: number) {
  if (apiConfig.sessionKey === null) {
    throw Error("sessionKey can't be null");
  }
  await apiConfig.ensureSession();

  const url = buildUrlWithParams(
    apiConfig.host + "/manage_popups/add_popup",
    {},
  );
  const payload = {
    file_id,
    // A pausable or cancellable popup represents an ongoing and unfinished job.
    // -> The popup will exist indefinitely and will not be user-dismissable unless the user can first cancel it.
    is_cancellable: false,
    is_pausable: false,
    // multiple notifications with same title merge "search" results.
    attached_files_mergable: true,
    status_title: "Your files from HydrusViewer",
    // this is what we group the files under:
    files_label: "Your files from HydrusViewer",
  };
  const { requestId, fetch } = useFetch(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "Hydrus-Client-API-Session-Key": apiConfig.sessionKey,
    },
    body: JSON.stringify(payload),
  });
  const response = await fetch;
  if (response.status !== 200) {
    console.error("Network response was not OK", response);
    throw new Error("Network response was not OK");
  }
  // TODO: tell the user it was successful.
}
</script>

<style lang="scss" scoped src="@/components/meta/meta-shared.scss" />
<style scoped>
.link-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem 0.65rem;

  /* Desktop (horizontal scroll): each URL on its own line */
  &.desktop-mode {
    flex-direction: column;
    align-items: flex-start;
  }
}

.link {
  position: relative;
  display: inline-flex;
  align-items: center;
  padding: 0;
  min-height: 0;
}
</style>
